// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).
export const SCHEMA_DRAFT = 'http://json-schema.org/draft-07/schema#';
const capitalize = (s) => (s.length === 0 ? s : s[0].toUpperCase() + s.slice(1));
export const groupDefinitionName = (name) => capitalize(name);
export const tableDefinitionName = (name) => `${capitalize(name)}Row`;
const LIST_ITEM = {
    'string[]': 'string',
    'number[]': 'number',
    'integer[]': 'integer',
    'boolean[]': 'boolean',
};
const LIST_TYPE = {
    string: 'string[]',
    number: 'number[]',
    integer: 'integer[]',
    boolean: 'boolean[]',
};
/** True for the list types (`string[]`, `number[]`, ...). */
export const isListType = (t) => LIST_ITEM[t] !== undefined;
function scalarToProperty(f) {
    // `enum` goes on the property for a single value and on `items` for a list (BDA rejects it on the array itself).
    const choice = f.options !== undefined ? { enum: [...f.options] } : {};
    const item = LIST_ITEM[f.type];
    if (item) {
        return {
            type: 'array',
            items: { type: item, ...choice },
            inferenceType: f.inferenceType,
            instruction: f.instruction,
        };
    }
    return { type: f.type, inferenceType: f.inferenceType, instruction: f.instruction, ...choice };
}
function scalarsToProperties(fields) {
    const out = {};
    for (const f of fields)
        out[f.name] = scalarToProperty(f);
    return out;
}
export function formToSchema(form) {
    const definitions = {};
    const properties = {};
    for (const node of form.fields) {
        if (node.kind === 'field') {
            properties[node.name] = scalarToProperty(node);
        }
        else if (node.kind === 'group') {
            const def = groupDefinitionName(node.name);
            // BDA rejects `description`/`instruction` on a definition and `description` next to `$ref`;
            // the group description goes in `instruction` next to `$ref` (verified in task 1.1, docs/BDA-NOTES.md).
            definitions[def] = { type: 'object', properties: scalarsToProperties(node.fields) };
            properties[node.name] = node.description
                ? { $ref: `#/definitions/${def}`, instruction: node.description }
                : { $ref: `#/definitions/${def}` };
        }
        else {
            const def = tableDefinitionName(node.name);
            definitions[def] = { type: 'object', properties: scalarsToProperties(node.columns) };
            properties[node.name] = { type: 'array', instruction: node.instruction, items: { $ref: `#/definitions/${def}` } };
        }
    }
    return {
        $schema: SCHEMA_DRAFT,
        description: form.description,
        class: form.documentType,
        type: 'object',
        definitions,
        properties,
    };
}
function bdaProperty(p) {
    const out = { ...p };
    if (out.type === 'integer')
        out.type = 'number';
    if (out.items)
        out.items = bdaProperty(out.items);
    if (out.properties)
        out.properties = Object.fromEntries(Object.entries(out.properties).map(([k, v]) => [k, bdaProperty(v)]));
    return out;
}
/**
 * The schema as BDA accepts it. BDA field types are string, number, boolean, and array: `CreateBlueprint`
 * rejects `type: "integer"` anywhere (top level, group, table column, list items) with "Request has invalid
 * blueprint schema" (task 11.3 smoke test; list items in task 13.7). The catalog keeps `integer` so the editor
 * round-trips "Whole number"; everything sent to BDA or handed to developers goes through this function.
 *
 * BDA also rejects an empty top-level `description` with the same message (task 12.3: a blueprint saved from
 * "Start from a document" without a description). The description is optional in the editor (requirement 3.1),
 * so an empty one is sent as "<document type> documents"; the catalog keeps the empty value.
 *
 * Allowed values (`enum`) are sent as they are. BDA constrains extraction to them (task 13.7 probe: a value outside
 * the list comes back as `""`), so no "Answer with one of" sentence is added to the instruction; adding one made
 * BDA force a wrong listed value instead of returning `""`.
 */
export function toBdaSchema(schema) {
    const map = (r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, bdaProperty(v)]));
    const description = schema.description.trim() || `${schema.class.trim() || 'Business'} documents`;
    return { ...schema, description, definitions: map(schema.definitions), properties: map(schema.properties) };
}
const ROOT_KEYS = new Set(['$schema', 'description', 'class', 'type', 'definitions', 'properties']);
const SCALAR_KEYS = new Set(['type', 'inferenceType', 'instruction', 'items', 'enum']);
const SCALAR_TYPES = new Set(['string', 'number', 'integer', 'boolean']);
const isRecord = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
function refName(ref) {
    if (typeof ref !== 'string')
        return null;
    const m = /^#\/definitions\/([^/]+)$/.exec(ref);
    return m ? m[1] : null;
}
function unknownKeys(obj, allowed, path, out) {
    for (const k of Object.keys(obj))
        if (!allowed.has(k))
            out.push(`${path}.${k}`);
}
function parseInference(v, path, out) {
    if (v === undefined)
        return 'explicit';
    if (v === 'explicit' || v === 'inferred')
        return v;
    out.push(`${path}.inferenceType`);
    return 'explicit';
}
function parseInstruction(v, path, out) {
    if (v === undefined)
        return '';
    if (typeof v === 'string')
        return v;
    out.push(`${path}.instruction`);
    return '';
}
/** `enum` as allowed values: only a list of strings on a string (BDA rejects enums on numbers and booleans). */
function parseEnum(v, type, path, out) {
    if (v === undefined)
        return undefined;
    if (type !== 'string' || !Array.isArray(v) || !v.every((x) => typeof x === 'string')) {
        out.push(`${path}.enum`);
        return undefined;
    }
    return [...v];
}
function parseScalar(name, prop, path, out) {
    if (!isRecord(prop)) {
        out.push(path);
        return null;
    }
    unknownKeys(prop, SCALAR_KEYS, path, out);
    let type;
    let options;
    if (prop.type === 'array') {
        const items = prop.items;
        if (!isRecord(items) ||
            typeof items.type !== 'string' ||
            !SCALAR_TYPES.has(items.type) ||
            Object.keys(items).some((k) => k !== 'type' && k !== 'enum')) {
            out.push(`${path}.items`);
            return null;
        }
        if (prop.enum !== undefined)
            out.push(`${path}.enum`);
        type = LIST_TYPE[items.type];
        options = parseEnum(items.enum, items.type, `${path}.items`, out);
    }
    else if (typeof prop.type === 'string' && SCALAR_TYPES.has(prop.type)) {
        if (prop.items !== undefined)
            out.push(`${path}.items`);
        type = prop.type;
        options = parseEnum(prop.enum, prop.type, path, out);
    }
    else {
        out.push(`${path}.type`);
        return null;
    }
    const field = {
        kind: 'field',
        name,
        type,
        inferenceType: parseInference(prop.inferenceType, path, out),
        instruction: parseInstruction(prop.instruction, path, out),
    };
    if (options !== undefined)
        field.options = options;
    return field;
}
function parseObjectDefinition(def, path, allowed, out) {
    if (!isRecord(def) || def.type !== 'object' || !isRecord(def.properties)) {
        out.push(path);
        return null;
    }
    unknownKeys(def, allowed, path, out);
    const fields = [];
    for (const [k, v] of Object.entries(def.properties)) {
        const f = parseScalar(k, v, `${path}.properties.${k}`, out);
        if (f)
            fields.push(f);
    }
    return fields;
}
/**
 * Converts a BDA schema back into the editor form. Returns the JSON paths the form
 * cannot represent instead of a lossy form (requirement 3.5).
 */
export function schemaToForm(schema, name = '') {
    const out = [];
    if (!isRecord(schema))
        return { unsupported: ['$'] };
    unknownKeys(schema, ROOT_KEYS, '$', out);
    if (schema.type !== 'object')
        out.push('$.type');
    if (schema.description !== undefined && typeof schema.description !== 'string')
        out.push('$.description');
    if (schema.class !== undefined && typeof schema.class !== 'string')
        out.push('$.class');
    const definitions = schema.definitions === undefined ? {} : schema.definitions;
    const properties = schema.properties === undefined ? {} : schema.properties;
    if (!isRecord(definitions))
        out.push('$.definitions');
    if (!isRecord(properties))
        out.push('$.properties');
    // Keep collecting after root-level problems so the user sees every unsupported path at once.
    if (!isRecord(definitions) || !isRecord(properties))
        return { unsupported: out };
    const usedDefs = new Set();
    const fields = [];
    for (const [key, prop] of Object.entries(properties)) {
        const path = `$.properties.${key}`;
        if (!isRecord(prop)) {
            out.push(path);
            continue;
        }
        if (prop.$ref !== undefined) {
            const def = refName(prop.$ref);
            if (!def || !(def in definitions) || usedDefs.has(def)) {
                out.push(path);
                continue;
            }
            unknownKeys(prop, new Set(['$ref', 'instruction']), path, out);
            usedDefs.add(def);
            const defObj = definitions[def];
            const groupFields = parseObjectDefinition(defObj, `$.definitions.${def}`, new Set(['type', 'description', 'properties']), out);
            if (!groupFields)
                continue;
            // `instruction` next to `$ref` is the BDA-accepted form; a definition `description` is read for older schemas.
            const description = prop.instruction !== undefined
                ? parseInstruction(prop.instruction, path, out)
                : isRecord(defObj) && typeof defObj.description === 'string'
                    ? defObj.description
                    : '';
            const group = { kind: 'group', name: key, description, fields: groupFields };
            fields.push(group);
            continue;
        }
        if (prop.type === 'array' && isRecord(prop.items) && prop.items.$ref !== undefined) {
            const def = refName(prop.items.$ref);
            unknownKeys(prop, new Set(['type', 'instruction', 'items']), path, out);
            if (!def || !(def in definitions) || Object.keys(prop.items).length !== 1 || usedDefs.has(def)) {
                out.push(`${path}.items`);
                continue;
            }
            usedDefs.add(def);
            const columns = parseObjectDefinition(definitions[def], `$.definitions.${def}`, new Set(['type', 'properties']), out);
            if (!columns)
                continue;
            const table = {
                kind: 'table',
                name: key,
                instruction: parseInstruction(prop.instruction, path, out),
                columns,
            };
            fields.push(table);
            continue;
        }
        const scalar = parseScalar(key, prop, path, out);
        if (scalar)
            fields.push(scalar);
    }
    for (const def of Object.keys(definitions))
        if (!usedDefs.has(def))
            out.push(`$.definitions.${def}`);
    if (out.length > 0)
        return { unsupported: out };
    return {
        form: {
            name,
            documentType: typeof schema.class === 'string' ? schema.class : '',
            description: typeof schema.description === 'string' ? schema.description : '',
            fields,
        },
    };
}
/** Allowed values by result path: `field`, `group.field`, and `table[].column` (the path `diffSchemas` uses). */
export function allowedValuesByPath(schema) {
    const res = schemaToForm(schema);
    const out = {};
    if (!res.form)
        return out;
    const add = (path, f) => {
        if (f.options && f.options.length > 0)
            out[path] = f.options;
    };
    for (const node of res.form.fields) {
        if (node.kind === 'field')
            add(node.name, node);
        else if (node.kind === 'group')
            node.fields.forEach((f) => add(`${node.name}.${f.name}`, f));
        else
            node.columns.forEach((f) => add(`${node.name}[].${f.name}`, f));
    }
    return out;
}
/**
 * The values of a result that are not among the allowed ones (compared trimmed and case-insensitively, since BDA
 * returns a listed value in the list's own case). `""` is not reported: it is how BDA says no allowed value matched.
 */
export function disallowedValues(value, options) {
    if (!options || options.length === 0)
        return [];
    const allowed = new Set(options.map((o) => o.trim().toLowerCase()));
    const values = Array.isArray(value) ? value : [value];
    return values.filter((v) => typeof v === 'string' && v.trim() !== '' && !allowed.has(v.trim().toLowerCase()));
}
//# sourceMappingURL=convert.js.map